/**
 * User role types for the permission system
 * - viewer: Read-only access to most of the platform
 * - sender: Can send messages and start conversations
 * - editor: Can schedule, add automations, send messages, manage templates
 * - administrator: Can do all of the above plus edit user permissions
 * - owner: Full access including WhatsApp/webhook linking
 * - support: Support Staff. Conversations only — not an Echo admin. Sees and
 *   answers every conversation, but no other page. Unrelated to a Solenex
 *   support session (the `impersonating` claim), which holds owner claims.
 */
export type UserRole = 'viewer' | 'sender' | 'editor' | 'administrator' | 'owner' | 'support';

/** How each role is named on screen. */
export const ROLE_LABELS: Record<UserRole, string> = {
    viewer: 'Viewer',
    sender: 'Sender',
    editor: 'Editor',
    administrator: 'Administrator',
    owner: 'Owner',
    support: 'Support Staff'
};

/** Display name for a role, tolerating values this build does not know. */
export function roleLabel(role: string | null | undefined): string {
    if (!role) return '';
    return ROLE_LABELS[role as UserRole] ?? role.charAt(0).toUpperCase() + role.slice(1);
}

/**
 * Complete user permissions interface
 * Calculated based on role and billing administrator status
 */
export interface UserPermissions {
    role: UserRole;
    isBillingAdministrator: boolean;
    canViewInvoices: boolean;
    canManageSubscriptions: boolean;
    canSendMessages: boolean;
    canScheduleMessages: boolean;
    canManageTemplates: boolean;
    canManageAutomations: boolean;
    canManageUsers: boolean;
    canLinkWhatsApp: boolean;
    canLinkWebhooks: boolean;
    /** Issue and revoke API credentials — same trust class as linking WhatsApp. */
    canManageApiApps: boolean;
    /**
     * Create and configure website chat widgets.
     *
     * A lower trust class than canManageApiApps: a widget key cannot send on
     * the organisation's behalf or spend its Meta allowance — it only receives —
     * so this sits with user management rather than with owner-only actions.
     */
    canManageWidgets: boolean;
    /**
     * Link an SMS provider (WinSMS, BulkSMS or SMSPortal) and change its
     * settings. Owner only, like canManageApiApps: the credential spends the
     * organisation's money on every send.
     */
    canManageSms: boolean;
    canViewConversations: boolean;
    /**
     * See every conversation, assign one to anyone, mark it dealt with and
     * recall it. Administrators, owner and Support Staff.
     */
    canManageConversations: boolean;
    /** Create a Planning Center workflow card from a conversation. */
    canCreateWorkflowCards: boolean;
    /**
     * Support Staff: the Conversations page is the whole app. Routes outside
     * it redirect there (conversationsOnlyGuard) and the menu shows nothing
     * else.
     */
    isConversationsOnly: boolean;
    canViewStats: boolean;
    canViewSettings: boolean;
}

/**
 * User profile stored in Firestore
 */
export interface UserProfile {
    uid: string;
    email: string;
    displayName: string;
    organizationId: string;
    role: UserRole;
    isBillingAdministrator: boolean;
    createdAt: any;
    updatedAt: any;
}
